import { configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import { Middleware } from 'redux';

import { baseApi } from '@/patient-app-common/api/baseApi';

import { coreReducer } from './reducers';

// Get or generate a session-specific encryption key
const getOrCreateSessionKey = (): string => {
  const storedKey = sessionStorage.getItem('sessionKey');
  if (storedKey) {
    return storedKey;
  }
  const newKey = self.crypto.randomUUID();
  sessionStorage.setItem('sessionKey', newKey);
  return newKey;
};

const SESSION_KEY = getOrCreateSessionKey();

interface PersistConfig {
  key: string;
  blacklist: string[];
}

// Simple XOR encryption/decryption function
const xorEncrypt = (text: string, key: string): string => {
  let result = '';
  for (let i = 0; i < text.length; i++) {
    result += String.fromCharCode(
      text.charCodeAt(i) ^ key.charCodeAt(i % key.length)
    );
  }
  return btoa(result); // Base64 encode the result
};

const xorDecrypt = (encoded: string, key: string): string => {
  const text = atob(encoded); // Base64 decode
  let result = '';
  for (let i = 0; i < text.length; i++) {
    result += String.fromCharCode(
      text.charCodeAt(i) ^ key.charCodeAt(i % key.length)
    );
  }
  return result;
};

const persistMiddleware =
  (persistConfig: PersistConfig): Middleware =>
  (store) =>
  (next) =>
  (action) => {
    const result = next(action);
    const state = store.getState();
    const stateToPersist = { ...state };

    const keyPersist = persistConfig.key || 'root:persist';

    // @ts-expect-error window cypress
    if (window.Cypress) {
      localStorage.setItem(keyPersist, JSON.stringify(stateToPersist));
      return result;
    }

    const blacklistPersist = persistConfig.blacklist || [];

    blacklistPersist.forEach((key) => {
      delete stateToPersist[key];
    });

    // Encrypt state before storing
    const encryptedState = xorEncrypt(
      JSON.stringify(stateToPersist),
      SESSION_KEY
    );
    sessionStorage.setItem(keyPersist, encryptedState);

    return result;
  };

const rehydrationState = (persistConfig: PersistConfig) => {
  const keyPersist = persistConfig.key || 'root:persist';

  // @ts-expect-error window cypress
  if (window.Cypress) {
    const serializedState = localStorage.getItem(keyPersist);
    if (!serializedState) return;

    return JSON.parse(serializedState);
  }

  try {
    const encryptedState = sessionStorage.getItem(keyPersist);
    if (!encryptedState) return;

    // Decrypt state
    const decryptedState = xorDecrypt(encryptedState, SESSION_KEY);
    return JSON.parse(decryptedState);
  } catch (e) {
    console.error('Error decrypting state:', e);
    return;
  }
};

const persistConfig = {
  key: 'root:persist',
  blacklist: [baseApi.reducerPath],
};
const preloadedState = rehydrationState(persistConfig);
export const store = configureStore({
  reducer: coreReducer,
  preloadedState,
  middleware: (getDefaultMiddleware) => {
    return (
      getDefaultMiddleware()
        .concat(persistMiddleware(persistConfig))
        // Adding the api middleware enables caching, invalidation, polling,
        // and other useful features of `rtk-query`.
        .concat(baseApi.middleware)
    );
  },
});

// optional, but required for refetchOnFocus/refetchOnReconnect behaviors
// see `setupListeners` docs - takes an optional callback as the 2nd arg for customization
setupListeners(store.dispatch);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
